import { Text, Box, VStack, Heading, HStack } from '@chakra-ui/react';
import DataTableCard from './DataTableCard';
import { useBinderCardColors } from '../hooks/useBinderCardColors';

interface LapsesCardProps {
  data?: any;
}

const LapsesCard = ({ data }: LapsesCardProps) => {
  const colors = useBinderCardColors();
  const drivers = data?.drivers || [];
  const insuranceSource = data?.insuranceSource;

  // Headers for different tables
  const autoPlusHeaders = [
    'Driver',
    'Start Date',
    'End Date',
    'Duration',
    'Status',
  ];
  const quoteHeaders = [
    'Driver',
    'Date',
    'Re-instate',
    'Duration',
    'Description',
  ];

  // Format empty value display
  const formatEmptyValue = (value: string) => {
    if (!value || value.trim() === '') {
      return (
        <Text color="gray.400" fontStyle="italic" fontSize="sm">
          Not provided
        </Text>
      );
    }
    return value;
  };

  // Create table rows from Insurance lapses data
  const createInsuranceDataRows = (lapses: any[]) => {
    if (lapses.length === 0) {
      return [];
    }

    return lapses.map((lapse: any) => [
      formatEmptyValue(lapse.driver),
      formatEmptyValue(lapse.startDate),
      formatEmptyValue(lapse.endDate),
      formatEmptyValue(lapse.duration),
      formatEmptyValue(lapse.status),
    ]);
  };

  // Create table rows from Quote lapses data
  const createQuoteRows = (lapses: any[]) => {
    if (lapses.length === 0) {
      return [];
    }

    return lapses.map((lapse: any) => [
      formatEmptyValue(lapse.driver),
      formatEmptyValue(lapse.date),
      formatEmptyValue(lapse.reinstateDate),
      formatEmptyValue(lapse.duration),
      <Box>
        <Text fontSize="sm">{lapse.description}</Text>
        {lapse.description?.toLowerCase().includes('payment') && (
          <Text fontSize="xs" color="orange.500" fontWeight="semibold" mt={1}>
            Payment-related cancellation
          </Text>
        )}
      </Box>,
    ]);
  };

  // Handle case when no drivers have lapses data
  if (drivers.length === 0) {
    return (
      <Box p={4} borderRadius="md" bg={colors.emptyFieldBgColor}>
        <Text color="gray.500" fontStyle="italic" textAlign="center">
          No lapse or cancellation data available
        </Text>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {drivers.map((driver: any) => (
        <Box key={driver.driverIndex}>
          {/* Driver Header with Summary */}
          <Box mb={3}>
            <HStack justify="space-between" align="baseline" mb={1}>
              <Heading size="sm" color={colors.textColor}>
                {driver.driverName}
              </Heading>
              {driver.totalLapses > 0 && (
                <Text fontSize="sm" color={colors.descriptionColor}>
                  Total Lapses: {driver.totalLapses}
                </Text>
              )}
            </HStack>
            {driver.hasLapseHistory && (
              <HStack spacing={3}>
                <Text fontSize="sm" color={colors.descriptionColor}>
                  {insuranceSource}: {driver.autoPlusLapses?.length || 0} lapse
                  {driver.autoPlusLapses?.length !== 1 ? 's' : ''}
                </Text>
                <Text fontSize="sm" color={colors.descriptionColor}>
                  •
                </Text>
                <Text fontSize="sm" color={colors.descriptionColor}>
                  Quote: {driver.quoteLapses?.length || 0} lapse
                  {driver.quoteLapses?.length !== 1 ? 's' : ''}
                </Text>
              </HStack>
            )}
          </Box>

          <VStack spacing={3} align="stretch">
            {/* Insurance Lapses Table */}
            {driver.autoPlusPolicies?.length > 0 && (
              <DataTableCard
                description={`Lapses (${insuranceSource})`}
                headers={autoPlusHeaders}
                rows={createInsuranceDataRows(driver.autoPlusPolicies)}
              />
            )}

            {/* Quote Lapses Table */}
            {driver.quoteLapses?.length > 0 && (
              <DataTableCard
                description="Lapses (Quote)"
                headers={quoteHeaders}
                rows={createQuoteRows(driver.quoteLapses)}
              />
            )}

            {/* Show message if no lapses */}
            {driver.autoPlusLapses?.length === 0 &&
              driver.quoteLapses?.length === 0 && (
                <Box p={3} bg={colors.emptyFieldBgColor} borderRadius="md">
                  <Text fontSize="sm" color="gray.500" fontStyle="italic">
                    No lapses or cancellations found for this driver
                  </Text>
                </Box>
              )}
          </VStack>
        </Box>
      ))}
    </VStack>
  );
};

export default LapsesCard;
